/** English strings for the accounts area. The Vietnamese file must have exactly the same keys. */
export const accounts = {
  crumb: 'Business',
  tierReseller: 'reseller tier',
  filter: {
    any: 'Any',
    search: 'Search',
    status: 'Status',
    type: 'Type',
    clearFilter: 'Remove filter: {{label}}',
    customer: 'Customer: {{name}}',
    subject: 'Registrant: {{name}}',
    owner: 'Owner: {{name}}',
    selectedRecord: 'selected record',
  },
  pager: { shown: '{{count}} shown' },
  common: {
    none: 'None',
    never: 'Never',
    yes: 'Yes',
    no: 'No',
    masked: 'Masked',
    maskedHint: 'Masked personal data: only a hint is stored for display',
    hiddenName: 'Name hidden',
    notFound: 'Not found',
    created: 'Created',
    updated: 'Updated',
    country: 'Country',
    currency: 'Currency',
    tier: 'Tier',
    status: 'Status',
    type: 'Type',
    id: 'ID',
    domains: 'Domains',
    customers: 'Customers',
    subjects: 'Subjects',
    documents: 'Documents',
    version: 'Version',
    versionN: 'v{{n}}',
    expires: 'Expires',
    overview: 'Overview',
    showAll: 'Show all',
    firstN: 'first {{count}}',
    newestN: 'newest {{count}}',
    noPermission: 'Your role cannot see billing data for this account.',
  },
  customers: {
    title: 'Customers',
    subtitle: 'Accounts that order and pay for domains. The registrant of each domain is recorded separately as a subject.',
    searchPlaceholder: 'Name, exact e-mail or ID',
    loadError: 'Could not load customers',
    empty: 'No customers match',
    col: { customer: 'Customer', typeCountry: 'Type · country', status: 'Status', domains: 'Domains', subjects: 'Subjects', currency: 'Currency', created: 'Created' },
  },
  customer: {
    fallbackTitle: 'Customer',
    loading: 'Loading customer…',
    notFound: 'Customer not found',
    loadError: 'Could not load the customer',
    account: 'Account',
    maskedNote: 'E-mail and phone are masked. This is the paying account; the registrant of each domain is listed under Subjects.',
    profile: {
      title: 'Billing details',
      language: 'Notification language',
      languageByCountry: 'By country',
      invoiceSource: 'Invoice details',
      invoiceSame: 'Same as the primary subject',
      invoiceOwn: 'Own details',
    },
    primary: {
      title: 'Primary subject',
      hint: 'Identity data (names, documents, birth date, address, representative) are edited on the subject; each edit makes a new version.',
      none: 'No primary subject',
      noneHint: 'This customer has no subject holding the account yet. Create one or pick one of the customer\'s subjects.',
      create: 'Create the primary subject',
      createTitle: 'Create the primary subject',
      createdReason: 'primary subject created from the customer page',
      pick: 'Or pick an existing subject',
      reason: 'Reason',
      link: 'Make it the primary subject',
      edit: 'Edit the subject',
      unverified: 'Not verified',
      representative: 'Representative',
      badge: 'Primary subject',
    },
    field: {
      code: 'Customer code',
      email: 'E-mail',
      phone: 'Phone',
      billingCurrency: 'Billing currency',
    },
    stat: { domains: 'Domains', subjects: 'Registrants managed', users: 'Users', wallet: 'Wallet balance', walletHint: 'available · {{held}} on hold', noWallet: 'No wallet', openWallet: 'Open wallet' },
    tab: { domains: 'Domains ({{count}})', subjects: 'Registrants ({{count}})', users: 'Users ({{count}})', billing: 'Billing' },
    menu: {
      title: 'Actions',
      edit: 'Edit details',
      addUser: 'Add a user',
      register: 'Register a domain',
      newSubject: 'New subject',
      related: 'Related',
      orders: 'All orders',
      subjects: 'All subjects',
      wallet: 'Wallet',
      webhooks: 'Customer webhooks',
      apiKeys: 'API keys',
    },
    noUsers: 'No users linked',
    userCol: { name: 'Name', relation: 'Role in account', kind: 'Kind', status: 'Status', lastLogin: 'Last sign-in', linked: 'Linked' },
    noSubjects: 'No registrants managed by this account',
    subjectCol: { legalName: 'Legal name', type: 'Type', status: 'Status', verification: 'Verification', domains: 'Domains', country: 'Country', created: 'Created' },
    noDomains: 'No domains',
    servicesTab: 'Services ({{count}})',
    noServices: 'No services',
    servicesMore: 'The first {{count}}: see every service in Sales & finance › Services.',
    serviceCol: { service: 'Service', plan: 'Product / plan', state: 'State', due: 'Paid until', amount: 'Price per cycle' },
    domainCol: { domain: 'Domain', registrant: 'Registrant', registry: 'Registry', lifecycle: 'Lifecycle', dossier: 'Dossier', expiry: 'Registry expiry' },
    billing: {
      loading: 'Loading billing…',
      loadError: 'Could not load billing',
      wallets: 'Wallets',
      noWallets: 'This account has no wallet yet.',
      wallet: 'Wallet {{currency}}',
      walletCol: { wallet: 'Wallet', status: 'Status', balance: 'Balance', held: 'On hold', available: 'Available' },
      orders: 'Orders ({{count}})',
      noOrders: 'No orders',
      orderCol: { order: 'Order', status: 'Status', items: 'Items', total: 'Total' },
      invoices: 'Invoices ({{count}})',
      noInvoices: 'No invoices',
      invoiceCol: { number: 'Number', type: 'Type', total: 'Total', order: 'Order', issued: 'Issued' },
      payments: 'Payments ({{count}})',
      noPayments: 'No payments',
      paymentCol: { payment: 'Payment', purpose: 'Purpose', provider: 'Provider', status: 'Status', created: 'Created' },
      invoiced: 'Net invoiced',
      viewAll: 'View all',
    },
  },
  user: {
    fallbackTitle: 'User',
    subtitle: 'Signs in for {{account}}',
    thisAccount: 'this account',
    loading: 'Loading user…',
    notFound: 'This user is not linked to the account',
    loadError: 'Could not load the user',
    profile: 'Profile',
    field: {
      account: 'Customer account',
      email: 'E-mail',
      kind: 'Kind',
      relation: 'Role in this account',
      linked: 'Linked to this account',
      mfa: 'Two-factor sign-in',
      locked: 'Locked',
      lastLogin: 'Last sign-in',
      sessions: 'Active sessions',
    },
    mfa: { enrolled: 'Enrolled', required: 'Required, not enrolled yet', optional: 'Not required' },
    maskedNote: 'The e-mail address is masked. Sign-in history is kept in the audit log.',
    accounts: 'Accounts this user can act for ({{count}})',
    accountCol: { customer: 'Customer', type: 'Type', status: 'Status', relation: 'Role in account', linked: 'Linked' },
    roles: 'Roles ({{count}})',
    noRoles: 'No roles granted',
    roleCol: { role: 'Role', scope: 'Applies to', granted: 'Granted', grantedBy: 'Granted by', revoked: 'Status' },
    revokedOn: 'Revoked {{date}}',
    scopeGlobal: 'Whole platform',
    scopeHidden: 'Outside your access',
    active: 'Active',
  },
  subjects: {
    title: 'Subjects',
    subtitle: 'The legal registrants (people and organisations) behind domains, with their current verification.',
    searchPlaceholder: 'Legal name or subject ID',
    loadError: 'Could not load subjects',
    empty: 'No subjects match',
    noVersion: '(no current version)',
    versionN: 'v{{n}}',
    piiNote: 'Identity numbers are never shown in lists. Open a subject to see masked hints; revealing a full number is recorded in the audit log.',
    col: { legalName: 'Legal name', typeCountry: 'Type · country', status: 'Status', verification: 'Verification', managedBy: 'Managed by', domains: 'Domains', created: 'Created' },
  },
  customerActions: {
    edit: 'Edit customer',
    editTitle: 'Edit customer details',
    editDescription: 'Changes are recorded in the audit log with the reason. E-mail, phone, addresses and the identity number are recorded only as "changed", never their values.',
    email: 'Contact e-mail',
    phone: 'Contact phone',
    identityOnSubject: 'Names, documents, address and representative belong to the primary subject: edit them on the subject page. Here you edit the account and its invoicing only.',
    currency: 'Billing currency',
    currencyLocked: 'The account has ledger, order or payment activity, so its currency cannot change.',
    tier: 'Tier',
    status: 'Status',
    statusHint: 'Suspending or closing the account needs a clear reason.',
    save: 'Save changes',
    saved: 'Customer details saved.',
    noChange: 'Nothing changed.',
    addUser: 'Add user',
    addUserTitle: 'Add a user to the customer',
    addUserDescription: 'A user who signs in to the customer account. A new user gets a first password shown only once; two-factor authentication is required at the first sign-in.',
    userEmail: 'Sign-in e-mail',
    userName: 'Full name',
    relation: 'Role in the account',
    create: 'Add user',
    created: 'User created and added to the customer.',
    linked: 'An existing customer user was added to this account (no new password).',
    alreadyShown: 'The first password was shown by the earlier request and cannot be shown again.',
    initialPassword: 'First password',
    initialPasswordHint: 'Give it to the user over a secure channel; they must change it and set up two-factor authentication at sign-in.',
    loading: 'Loading customer details…',
  },
  walletAdjust: {
    action: 'Adjust balance',
    title: 'Adjust wallet balance',
    description: 'Credit or debit the wallet of {{customer}} by hand, e.g. for a bank transfer received. Posted to the ledger against manual clearing and recorded in the audit log with the reason.',
    current: 'Balance {{balance}} · held {{held}} · available {{available}}',
    noWallet: 'This customer has no wallet yet: a credit opens one in {{currency}}.',
    direction: 'Adjustment',
    credit: 'Credit (add)',
    debit: 'Debit (subtract)',
    amount: 'Amount ({{currency}})',
    amountHint: 'Greater than zero; group separators are allowed (1.000.000).',
    amountInvalid: 'Enter an amount greater than zero, in whole {{currency}} units.',
    amountHintMinor: 'Greater than zero, at most {{digits}} decimal places after a point; commas may group thousands ({{example}}).',
    amountInvalidMinor: 'Enter an amount in {{currency}} greater than zero, at most {{digits}} decimal places after a point (e.g. {{example}}).',
    amountTooLarge: 'The amount is too large: the ledger holds at most 14 integer digits (under 100 trillion).',
    debitLimit: 'At most {{available}} can be taken: the available balance (balance minus holds).',
    reference: 'Transaction code',
    referenceHint: 'Required: the transaction code on the bank statement or receipt, to match it.',
    large: 'Above {{threshold}} (or in another currency) a second person approves first.',
    requested: 'Approval requested. Once someone else approves it (Approvals), post the adjustment again with the same amount and transaction code.',
    ordersFunded_one: 'Paid {{count}} order waiting for funds from the wallet.',
    ordersFunded_other: 'Paid {{count}} orders waiting for funds from the wallet.',
    confirmCredit: 'I confirm adding {{amount}} to the wallet of {{customer}}.',
    confirmDebit: 'I confirm taking {{amount}} from the wallet of {{customer}}.',
    submit: 'Post adjustment',
    done: 'Posted. The wallet now has {{balance}}, of which {{available}} is available.',
    openEntry: 'Open the journal entry',
  },
  newCustomer: {
    button: 'New customer',
    title: 'New customer',
    description: 'A customer is the account that buys and pays together with its primary subject (the account holder). Identity data are entered once, on the subject; the customer can add other subjects.',
    currency: 'Billing currency',
    cancel: 'Cancel',
    create: 'Create customer',
    createLogin: 'Create a sign-in account for the customer',
    createLoginHint: 'Owner user with the e-mail {{email}}; the first password is shown once. An existing customer user with this e-mail is added instead.',
    created: 'Customer created.',
    duplicatesTitle: 'Other customers use the same details',
    duplicateField: { email: 'Same e-mail', tax_code: 'Same tax code' },
    loginLinked: 'An existing customer user with this e-mail was added as the owner (no new password).',
    subjectCreated: 'Primary subject created, not yet verified',
    subjectVerify: 'Verify the subject before registering domains.',
    openSubject: 'Open the subject',
    openRepresentative: 'Open the representative\'s subject',
    primarySubject: 'Primary subject',
    primarySubjectHint: 'The person or organisation holding the account: the same fields and checks as "New subject". An empty e-mail or phone takes the account\'s.',
    openCustomer: 'Open the customer',
  },
  customerForm: {
    section: {
      account: 'Account',
      invoice: 'Invoicing',
      login: 'Sign-in account',
    },
    email: 'E-mail',
    phone: 'Phone',
    phoneHintVn: 'E.g. 0912 345 678 or 028 3822 1234; stored as +84…',
    phoneHint: 'With the country code, e.g. +65 6123 4567',
    language: 'Notification language',
    languages: { vi: 'Vietnamese', en: 'English' },
    tier: 'Price tier',
    province: 'Province / city',
    ward: 'Ward / commune',
    provinceFirst: 'Choose the province first',
    line1: 'Address line 1',
    line2: 'Address line 2',
    choose: '— Choose —',
    loading: 'Loading…',
    invoiceSame: 'Same as the primary subject',
    invoiceBuyer: 'Buyer name',
    invoiceTaxCode: 'Tax code',
    invoiceAddress: 'Invoice address',
    invoiceEmails: 'Invoice e-mails',
    invoiceEmailsHint: 'Separate with commas; up to 5 ({{count}} entered).',
    invoiceTaxCodePersonHint: 'An individual / household business: the (owner\'s) CCCD, 12 digits.',
    invoiceTaxCodeOrgHint: '10 digits, or 10-3 for a branch.',
    duplicate: 'Also used by:',
    fixFields: '{{count}} fields need attention.',
    errors: {
      REQUIRED: 'Required',
      EMAIL_FORMAT: 'Not a valid e-mail address',
      PHONE_FORMAT: 'Not a valid phone number (other countries: start with + and the country code)',
      COUNTRY_FORMAT: 'Choose a country',
      LANGUAGE_UNKNOWN: 'Vietnamese or English',
      TAX_CODE_FORMAT: 'Enterprise / organisation tax code: 10 digits, or 10-3 digits for a branch (e.g. 0312345678-001)',
      TAX_CODE_CCCD_FORMAT: 'The tax code of a person / household business is the (owner\'s) citizen ID: exactly 12 digits',
      TAX_CODE_ANY_FORMAT: 'Tax code: 10 or 10-3 digits (enterprise), or a 12-digit citizen ID (person, household business)',
      TAX_CODE_NOT_OWNER_CCCD: 'Must be the owner\'s citizen ID',
      HOUSEHOLD_CODE_FORMAT: '6 to 20 letters, digits or hyphens',
      BUSINESS_KIND_UNKNOWN: 'Enterprise or household business',
      CCCD_GENDER_MISMATCH: 'The citizen ID does not match the gender',
      CCCD_BIRTH_YEAR_MISMATCH: 'The citizen ID does not match the birth year',
      BIRTH_DATE_INVALID: 'Invalid date of birth',
      TOO_YOUNG: 'The legal representative must be at least 18',
      CCCD_FORMAT: 'A CCCD has 12 digits',
      IDENTITY_FORMAT: '6 to 20 letters or digits',
      PROVINCE_UNKNOWN: "Not in VNNIC's list",
      WARD_UNKNOWN: "Not in VNNIC's list",
      WARD_NOT_IN_PROVINCE: 'This ward is not in the chosen province',
      PLACE_RETIRED: "No longer in VNNIC's list",
      POSTAL_CODE_FORMAT: 'Postal code in the wrong form (Vietnam: 5 digits, or 6 in the former system)',
      STREET_REQUIRED: 'Required',
      TOO_MANY: 'Too many',
      TIER_UNKNOWN: 'Choose one of the existing tiers',
      CURRENCY_UNKNOWN: 'Unknown currency',
      NOT_FOUND: 'Not found',
      INVALID_VALUE: 'Not a valid value; choose or enter it again',
    },
  },
  taxLookup: {
    button: 'Look up',
    running: 'Looking up…',
    title: 'What the business registry holds',
    close: 'Close',
    name: 'Registered name',
    shortName: 'Short name',
    status: 'Status',
    foundedOn: 'Founded on',
    registeredOn: 'Registered on',
    representative: 'Legal representative',
    owner: 'Household business owner',
    nameMatches: '✓ same as the name typed',
    nameDiffers: 'differs from the name typed — check it',
    address: 'Registered address',
    addressNote: 'in the former administrative list; type the address in the current list',
    notActive: 'The enterprise is not in operation: it cannot be used to register a domain.',
    use: 'Use the registered name',
    useHint: 'Fills the registered name (and short name, founding date). Type the representative’s names as on the identity document.',
    notFound: 'This tax code is not in the business registry.',
    notConnected: 'This server is not connected to the business registry (Integrations → VNNIC).',
    forbidden: 'You are not allowed to look up the business registry.',
    badCode: 'The tax code is not in a valid form.',
    failed: 'The lookup failed. Try again later.',
    verification: 'Look up the business registry',
    verificationDone: 'The lookup was kept as verification evidence: {{found}}, name {{name}}.',
    verificationFound: 'found',
    verificationNotFound: 'not found',
    verificationNameMatches: 'matches',
    verificationNameDiffers: 'differs',
    verificationNameUnknown: 'unknown',
  },
  subjectForm: {
    firstName: 'First name',
    lastName: 'Last name',
    fullNamePreview: 'Full name: {{name}} — must match the ID card / passport.',
    namesHint: 'Exactly as on the document: last name (e.g. Nguyễn) and first name (e.g. Văn An).',
    contactPerson: "The organisation's contact person",
    contactPersonHint: 'The contact person (usually the legal representative): last and first name separately.',
    reverifyNotice: 'A new last name, first name, document number or date of birth needs a new eKYC before registering a domain.',
    keepVerificationNotice: 'Name, document and date of birth unchanged: the current verification is kept for the new version.',
    keepDeclarationNotice: 'Name, codes, address, phone, e-mail and representative / owner unchanged: the signed subject declaration (if any) still applies to the new version.',
    resignDeclarationNotice: 'A new name, code, address, phone, e-mail or representative / owner means signing the subject declaration again before registering a new domain.',
    createTitle: 'New subject',
    editTitle: 'Edit subject',
    createButton: 'New subject',
    editButton: 'Edit',
    description: 'Every field needed to register domains, the same for every domain. A Vietnamese address: province and ward from the administrative list.',
    kind: 'Kind of subject',
    kinds: { INDIVIDUAL: 'Individual', ORGANIZATION: 'Organisation' },
    customerSearch: 'Find customer',
    customerSearchPlaceholder: 'Name or e-mail',
    customer: 'Managed by customer',
    choose: '— Choose —',
    loading: 'Loading…',
    person: 'Person',
    organization: 'Organisation',
    fullName: 'Full name (as on the ID card)',
    orgName: 'Registered name',
    tradeName: 'Trade name / abbreviation',
    establishDate: 'Founding date',
    birthDate: 'Date of birth',
    gender: 'Gender',
    genders: { Male: 'Male', Female: 'Female' },
    nationality: 'Nationality (2 letters)',
    position: 'Position',
    contactPosition: 'Representative’s position',
    identity: 'Identity document',
    idType: 'Document',
    idTypes: { NATIONAL_ID: 'Citizen ID (CCCD)', PASSPORT: 'Passport', TAX_CODE: 'Tax code', ENTERPRISE_CODE: 'Enterprise code' },
    idValue: 'Number',
    idIssuedAt: 'Issued on',
    cccdHint: '12 digits; must match the gender and year of birth.',
    idKept: 'On file: {{hint}}. Leave empty to keep it.',
    keptOnFile: 'A date is on file. Leave empty to keep it.',
    address: 'Address',
    country: 'Country (2 letters)',
    postalCode: 'Postal code',
    postalCodeHint: 'Vietnam: 5 digits (national postal code); left empty, 76000 is used.',
    postalCodeVn: 'Postal code (optional, default {{code}})',
    orgKind: 'Organisation type',
    orgKinds: { ENTERPRISE: 'Enterprise / organisation', HOUSEHOLD_BUSINESS: 'Household business' },
    owner: 'Household business owner',
    householdCode: 'Household business registration number',
    householdTaxCode: 'Household business tax code',
    householdTaxCodeHint: 'It is the owner\'s CCCD; left empty, the CCCD above is used.',
    representativeOwnerHint: 'The owner entered above becomes an individual subject linked as the representative.',
    street: 'House number, street',
    streetHint: 'Only the part below the ward; ward and province come from the list.',
    province: 'Province / city',
    ward: 'Ward / commune',
    wardCount: '{{count}} wards in this province',
    provinceFirst: 'Choose a province first',
    city: 'City',
    state: 'State / region',
    contact: 'Contact',
    email: 'E-mail',
    phone: 'Phone',
    phoneHint: 'e.g. 0912 345 678; stored as +84.912345678',
    fax: 'Fax',
    representative: 'Legal representative',
    representativeHint: 'An organisation needs its legal representative as a separate individual subject (the domain\'s administrative contact).',
    representativeCurrent: 'Current: {{name}}',
    representativeNone: 'None linked yet',
    representativeModes: { keep: 'Keep', existing: 'Pick an individual subject of this customer', new: 'Enter a new representative', owner: 'The owner is the representative' },
    representativePick: 'Individual subject',
    representativeCustomerFirst: 'Choose the managing customer first',
    representativeSameAddress: 'Same address as the organisation',
    cccdTaxHint: '12 digits; must match gender and birth year. An individual\'s CCCD is also the tax code.',
    taxCodeHint: '10 digits, or 10-3 for a branch (13 digits typed are stored as 10-3).',
    reason: 'Reason for the change',
    reasonPlaceholder: 'e.g. customer updated the address',
    fixFields: '{{count}} field(s) need attention.',
    cancel: 'Cancel',
    create: 'Create subject',
    saveVersion: 'Save as new version',
    errors: {
      REQUIRED: 'Required',
      CCCD_FORMAT: 'A CCCD has 12 digits',
      CCCD_GENDER_MISMATCH: 'The CCCD does not match the gender',
      CCCD_BIRTH_YEAR_MISMATCH: 'The CCCD does not match the year of birth',
      TAX_CODE_FORMAT: 'Enterprise / organisation tax code: 10 digits, or 10-3 digits for a branch',
      TAX_CODE_CCCD_FORMAT: 'The tax code of a person / household business is the (owner\'s) citizen ID: exactly 12 digits',
      TAX_CODE_NOT_OWNER_CCCD: 'Must be the owner\'s citizen ID',
      REPRESENTATIVE_NOT_ALLOWED: 'Pick an individual subject of the same customer',
      PASSPORT_FORMAT: '6 to 20 characters',
      IDENTIFIER_TYPE_NOT_ALLOWED: 'Not allowed for this kind of subject',
      EMAIL_FORMAT: 'Not a valid e-mail address',
      PHONE_FORMAT: 'Not a valid phone number',
      BIRTH_DATE_INVALID: 'Not a valid date of birth',
      ESTABLISH_DATE_INVALID: 'Not a valid founding date',
      TOO_YOUNG: 'Too young',
      PROVINCE_UNKNOWN: 'Not in VNNIC’s list',
      WARD_UNKNOWN: 'Not in VNNIC’s list',
      WARD_NOT_IN_PROVINCE: 'This ward is not in the chosen province',
      PLACE_RETIRED: 'No longer in VNNIC’s list',
      POSTAL_CODE_FORMAT: 'Postal code in the wrong form (Vietnam: 5 digits, or 6 in the former system)',
      STREET_REQUIRED: 'Required',
      HOUSEHOLD_CODE_FORMAT: '6 to 20 letters, digits or hyphens',
      NOT_FOUND: 'Not found',
      INVALID_VALUE: 'Not a valid value; choose or enter it again',
    },
  },
  dossiers: {
    title: 'Dossiers',
    subtitle: 'The registration dossier of each domain. A completed dossier is frozen; corrections create a new version.',
    domainFilter: 'Domain starts with',
    domainPlaceholder: 'example',
    includeSuperseded: 'Include superseded',
    loadError: 'Could not load dossiers',
    empty: 'No dossiers match',
    notBound: 'Not bound',
    views: 'Dossier lists',
    view: { ALL: 'All', SIGNED: 'Signed', NOT_SIGNED: 'Not signed', EKYC: 'eKYC list', SENSITIVE_KEYWORD: 'Names with a sensitive keyword', NOT_SYNCED: 'Not synced', VNNIC: 'On VNNIC' },
    createEkyc: 'Create eKYC',
    createEkycHint: 'eKYC dossiers waiting for the customer: remind the customer to complete eKYC',
    signed: 'Signed by the registrar',
    notSigned: 'Not signed by the registrar',
    onVnnic: 'Sent to VNNIC',
    notSynced: 'Not synced',
    col: { domain: 'Domain', version: 'Version', type: 'Type', status: 'Status', registrant: 'Registrant', customer: 'Customer', documents: 'Documents', vnnic: 'VNNIC', completed: 'Completed', updated: 'Updated' },
  },
  dossier: {
    title: 'Dossier · {{domain}}',
    fallbackTitle: 'Dossier',
    loading: 'Loading dossier…',
    notFound: 'Dossier not found',
    loadError: 'Could not load the dossier',
    supersededTitle: 'Superseded version',
    superseded: 'This version was superseded by <link>version {{n}}</link>.',
    section: 'Dossier',
    frozen: 'frozen',
    field: {
      domain: 'Domain',
      lifecycle: 'Domain lifecycle',
      customer: 'Customer',
      policy: 'Policy',
      supersedes: 'Replaces',
      verified: 'Verified',
      completed: 'Completed',
    },
    registrant: 'Registrant',
    noSubject: 'No registrant bound yet.',
    verificationProvider: { DKKD: 'National business registration portal', VNEID: 'VNeID' },
    registrantField: {
      subject: 'Registrant',
      pinnedVersion: 'Registrant version used',
      verification: 'Verification',
      method: 'Verification method',
      provider: 'Checked with',
      reviewer: 'Reviewed by',
      expires: 'Verification expires',
    },
    tab: { documents: 'Documents ({{count}})', form: 'Form and consents', history: 'Status history', versions: 'All versions ({{count}})' },
    noDocuments: 'No documents bound',
    docCol: { purpose: 'Purpose', document: 'Document', classification: 'Classification', version: 'Version', verification: 'Check', expires: 'Expires', bound: 'Bound' },
    formFields: 'Form fields captured',
    consents: 'Consents',
    consentFallback: 'Consent',
    consentMeta: 'text {{version}} · {{actor}} · {{at}}',
    noConsents: 'None recorded',
    formNote: 'Field values may contain personal data and are not shown here; only the field names are listed.',
    noHistory: 'No status changes recorded',
    missingItem: {
      subject: "The dossier's registrant",
      identity_verification: "The registrant's identity verification",
      manual_review: 'A staff review of the dossier',
      evidence: 'Documents of the dossier (none yet)',
    },
    historyCol: { when: 'When', from: 'From', to: 'To', missing: 'Missing', reason: 'Reason', actor: 'By' },
    versionCol: { version: 'Version', status: 'Status', created: 'Created', completed: 'Completed' },
    thisVersion: 'v{{n}} (this)',
    formField: {
      operation: 'Operation',
      registrant_address: 'Registrant address',
      registrant_email: 'Registrant e-mail',
      registrant_name: 'Registrant name',
      registrant_phone: 'Registrant phone',
    },
  },
  documents: {
    title: 'Documents',
    subtitle: 'Identity and registration documents kept for registrants, dossiers and accounts, with their checks.',
    owner: 'Owner',
    documentType: 'Document type',
    classification: 'Classification',
    loadError: 'Could not load documents',
    empty: 'No documents match',
    versionOf: 'v{{n}} of {{total}}',
    col: { document: 'Document', owner: 'Belongs to', classification: 'Classification', current: 'Current version', verification: 'Check', expires: 'Expires', dossiers: 'Dossiers', uploaded: 'Uploaded' },
  },
  document: {
    fallbackTitle: 'Document',
    loading: 'Loading document…',
    notFound: 'Document not found',
    loadError: 'Could not load the document',
    section: 'Document',
    field: { type: 'Type', classification: 'Classification', owner: 'Belongs to', versions: 'Versions', created: 'Created' },
    encryptedTitle: 'Files are stored encrypted',
    encrypted: 'Open a document to view or download its file: this needs the permission to view identity documents and every opening is logged with the reason.',
    view: 'View',
    download: 'Download',
    readTitle: 'Open {{name}}',
    readBody: 'Opening a stored file is logged with the reason and needs the permission to view identity documents.',
    previewHint: 'The stored file, decrypted for viewing.',
    tab: { versions: 'Versions ({{count}})', bindings: 'Used in dossiers ({{count}})' },
    noVersions: 'No versions',
    current: 'current',
    by: 'by {{actor}}',
    score: 'score {{score}}',
    reviewedBy: 'reviewed by {{name}}',
    versionCol: { version: 'Version', file: 'File', sha: 'SHA-256', issued: 'Issued', expires: 'Expires', uploaded: 'Uploaded', verifications: 'Checks' },
    noBindings: 'Not used in any dossier',
    bindingCol: { domain: 'Domain', dossier: 'Dossier', purpose: 'Purpose', version: 'Document version', bound: 'Bound' },
  },
  enum: {
    customerStatus: { ACTIVE: 'Active', SUSPENDED: 'Suspended', CLOSED: 'Closed' },
    subjectStatus: { ACTIVE: 'Active', MERGED: 'Merged', DEACTIVATED: 'Deactivated' },
    verification: { UNVERIFIED: 'Unverified', PENDING: 'Pending', VERIFIED: 'Verified', FAILED: 'Failed', MISMATCH: 'Mismatch', EXPIRED: 'Expired', REVOKED: 'Revoked' },
    docVerification: { PENDING: 'Pending', PASSED: 'Passed', FAILED: 'Failed', INCONCLUSIVE: 'Inconclusive' },
    classification: { PUBLIC: 'Public', INTERNAL: 'Internal', CONFIDENTIAL: 'Confidential', HIGHLY_SENSITIVE: 'Highly sensitive', SECRET: 'Secret' },
    dossierStatus: {
      NOT_CREATED: 'Not created',
      COLLECTING: 'Collecting',
      PENDING_IDENTITY: 'Pending identity',
      PENDING_DOCUMENT: 'Pending document',
      PENDING_SIGNATURE: 'Pending signature',
      PENDING_REVIEW: 'Pending review',
      COMPLETE: 'Complete',
      INVALID: 'Invalid',
      EXPIRED: 'Expired',
      CORRECTION_REQUIRED: 'Correction required',
      SUPERSEDED: 'Superseded',
    },
    accountType: { INDIVIDUAL: 'Individual', ORGANIZATION: 'Organization' },
    subjectType: { INDIVIDUAL: 'Individual', ORGANIZATION: 'Organization', HOUSEHOLD_BUSINESS: 'Household business', GOVERNMENT_ENTITY: 'Government entity', FOREIGN_ENTITY: 'Foreign entity', OTHER: 'Other' },
    dossierType: { VN_DOMAIN: '.vn domain', INTERNATIONAL_GTLD: 'International gTLD', INTERNATIONAL_NEW_GTLD: 'International new gTLD', INTERNATIONAL_CCTLD: 'International ccTLD', OTHER: 'Other' },
    ownerType: { SUBJECT: 'Registrant', DOSSIER: 'Dossier', CUSTOMER: 'Customer', CASE: 'Abuse case' },
    userKind: { STAFF: 'Staff', CLIENT: 'Client', CUSTOMER: 'Customer' },
    relation: { OWNER: 'Owner', ADMIN: 'Admin', BILLING: 'Billing', TECHNICAL: 'Technical', READ_ONLY: 'Read only' },
    userStatus: { ACTIVE: 'Active', INVITED: 'Invited', SUSPENDED: 'Suspended', DISABLED: 'Disabled', LOCKED: 'Locked' },
    role: {
      customer: 'Customer',
      support: 'Customer support',
      billing: 'Billing operations',
      compliance: 'Compliance officer',
      registry_ops: 'Registry operations',
      security: 'Security',
      administrator: 'Platform administrator',
      auditor: 'Auditor (read only)',
      ops_viewer: 'Operations viewer',
      ops_engineer: 'Operations engineer',
      ops_admin: 'Operations administrator',
    },
    scopeType: { GLOBAL: 'Platform', CUSTOMER: 'Customer' },
    documentType: {
      NATIONAL_ID_CARD: 'National ID card',
      PASSPORT: 'Passport',
      BUSINESS_REGISTRATION_CERTIFICATE: 'Business registration certificate',
      REGISTRATION_DECLARATION: 'Registration declaration',
      AUTHORIZATION_LETTER: 'Authorisation letter',
      OTHER: 'Other',
      REGISTRAR_SIGNED_FILE: 'Registrar-signed file',
      REGISTRAR_SIGNED_DECLARATION: 'Registrar-signed declaration',
      SIGNED_DECLARATION: 'Signed declaration',
      SIGNED_SUBJECT_DECLARATION: 'Signed registrant declaration',
      NATIONAL_ID_SCAN: 'ID document scan',
      BUSINESS_REGISTRATION: 'Business registration certificate',
      EKYC_ID_CARD_FRONT: 'eKYC ID image, front',
      EKYC_ID_CARD_BACK: 'eKYC ID image, back',
      EKYC_SELFIE: 'eKYC portrait',
      REGISTRATION_FORM: 'Domain declaration',
      DOMAIN_DECLARATION: 'Domain declaration',
      TRANSFEREE_DECLARATION: 'Transferee declaration',
      NEW_HOLDER_DECLARATION: 'New holder declaration',
      IDENTITY_CARD_FRONT: 'Identity card (front)',
      IDENTITY_CARD_BACK: 'Identity card (back)',
      REGISTRAR_CHANGE_FORM: 'Registrar change declaration',
      CHANGE_INFORMATION_REQUEST: 'Request to change domain information',
      SENSITIVE_KEYWORD_COMMITMENT: 'Sensitive keyword commitment',
      RETURN_REQUEST: 'Domain return request',
      RETURN_REQUEST_FORM: 'Domain return request (form)',
      GOV_VN_COMMITMENT: 'gov.vn explanation and commitment minutes',
      LOOKING_DOMAIN_FORM: 'Looking domain service declaration',
      NAME_VN_EXPLANATION: '.name.vn registration explanation',
      SIGNED_ORGANIZATION_INFO: 'Signed organisation information',
      REGISTRANT_NAME_CHANGE_REQUEST: 'Request to change the registrant name',
      NAME_CHANGE_DECISION: 'Name change decision',
      TRANSFER_TAX_COMMITMENT: 'Transfer tax commitment',
      TRANSFER_TAX_RECEIPT: 'Transfer tax receipt',
      TRANSFER_AGREEMENT: 'Domain transfer request',
      TRANSFER_APPROVAL: 'Domain transfer approval',
      DOMAIN_CERTIFICATE: 'Domain certificate',
      SERVICE_CONTRACT: 'Domain service contract',
      ORGANIZATION_INFO: 'Organisation file (organisation information declaration)',
    },
    purpose: {
      IDENTITY_DOCUMENT: 'Identity document',
      IDENTITY_PROOF: 'Proof of identity',
      REGISTRATION_DECLARATION: 'Registration declaration',
      ADDRESS_PROOF: 'Proof of address',
      AUTHORIZATION: 'Authorisation',
      OTHER: 'Other',
      REGISTRAR_SIGNED_FILE: 'Registrar-signed file',
      REGISTRAR_SIGNED_DECLARATION: 'Registrar-signed declaration',
      SIGNED_DECLARATION: 'Signed declaration',
      SIGNED_SUBJECT_DECLARATION: 'Signed registrant declaration',
      NATIONAL_ID_SCAN: 'ID document scan',
      BUSINESS_REGISTRATION: 'Business registration certificate',
      EKYC_ID_CARD_FRONT: 'eKYC ID image, front',
      EKYC_ID_CARD_BACK: 'eKYC ID image, back',
      EKYC_SELFIE: 'eKYC portrait',
      REGISTRATION_FORM: 'Domain declaration',
      DOMAIN_DECLARATION: 'Domain declaration',
      TRANSFEREE_DECLARATION: 'Transferee declaration',
      NEW_HOLDER_DECLARATION: 'New holder declaration',
      IDENTITY_CARD_FRONT: 'Identity card (front)',
      IDENTITY_CARD_BACK: 'Identity card (back)',
      REGISTRAR_CHANGE_FORM: 'Registrar change declaration',
      CHANGE_INFORMATION_REQUEST: 'Request to change domain information',
      SENSITIVE_KEYWORD_COMMITMENT: 'Sensitive keyword commitment',
      RETURN_REQUEST: 'Domain return request',
      RETURN_REQUEST_FORM: 'Domain return request (form)',
      GOV_VN_COMMITMENT: 'gov.vn explanation and commitment minutes',
      LOOKING_DOMAIN_FORM: 'Looking domain service declaration',
      NAME_VN_EXPLANATION: '.name.vn registration explanation',
      SIGNED_ORGANIZATION_INFO: 'Signed organisation information',
      REGISTRANT_NAME_CHANGE_REQUEST: 'Request to change the registrant name',
      NAME_CHANGE_DECISION: 'Name change decision',
      TRANSFER_TAX_COMMITMENT: 'Transfer tax commitment',
      TRANSFER_TAX_RECEIPT: 'Transfer tax receipt',
      TRANSFER_AGREEMENT: 'Domain transfer request',
      TRANSFER_APPROVAL: 'Domain transfer approval',
      DOMAIN_CERTIFICATE: 'Domain certificate',
      SERVICE_CONTRACT: 'Domain service contract',
      PASSPORT: 'Passport',
    },
    actorType: { CUSTOMER: 'Customer', OPERATOR: 'Operator', SYSTEM: 'System', RESELLER: 'Reseller', STAFF: 'Staff', USER: 'User', REGISTRY: 'Registry' },
    verificationType: { OCR: 'OCR', MANUAL: 'Manual review', FACE_MATCH: 'Face match', REGISTRY_LOOKUP: 'Registry lookup', AUTHENTICITY: 'Authenticity check', SIGNATURE: 'Signature check', REGISTRAR_SIGNATURE: 'Registrar signature', STRUCTURE: 'File structure check' },
    verificationMethod: {
      EKYC_CCCD_CHIP: 'eKYC (chip-based citizen ID)',
      BUSINESS_REGISTRY_LOOKUP: 'Business registry lookup',
      MANUAL_REVIEW: 'Manual review (removed)',
      DOCUMENT_UPLOAD: 'Document upload',
      DIGITAL_SIGNATURE: 'Digital signature',
      ELECTRONIC_IDENTITY: 'Electronic identity',
      EKYC: 'eKYC',
      LEGAL_DOCUMENT: 'Legal document (removed)',
    },
    consentType: { REGISTRATION_AGREEMENT: 'Registration agreement', DATA_PROCESSING: 'Data processing', MARKETING: 'Marketing', TERMS_OF_SERVICE: 'Terms of service' },
  },
} as const;
